import React, { useState, useEffect, useCallback } from 'react';
import styled from 'styled-components';
import { useNavigate } from 'react-router-dom';
import {
  MdPersonSearch, MdReceipt, MdRequestQuote, MdPeople,
  MdRefresh, MdOpenInNew, MdWarning, MdCheckCircle
} from 'react-icons/md';
import { miDiaAPI } from '../services/api';

// ── Layout ────────────────────────────────────────────────────────────────────

const Container = styled.div`width: 100%;`;

const PageHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: ${p => p.theme.spacing.xl};
  gap: ${p => p.theme.spacing.md};
  flex-wrap: wrap;
`;

const TitleBlock = styled.div``;
const Title = styled.h1`
  font-size: ${p => p.theme.fontSizes.xxxl};
  color: ${p => p.theme.colors.textPrimary};
  margin: 0 0 4px;
  @media (max-width: ${p => p.theme.breakpoints.mobile}) { font-size: ${p => p.theme.fontSizes.xxl}; }
`;
const Subtitle = styled.p`
  margin: 0;
  font-size: ${p => p.theme.fontSizes.sm};
  color: ${p => p.theme.colors.textSecondary};
`;

const RefreshBtn = styled.button`
  width: 38px; height: 38px; min-height: unset;
  border: 1px solid ${p => p.theme.colors.border};
  border-radius: ${p => p.theme.borderRadius.md};
  background: ${p => p.theme.colors.backgroundLight};
  color: ${p => p.theme.colors.textSecondary};
  display: flex; align-items: center; justify-content: center;
  font-size: 1.2rem; transition: ${p => p.theme.transitions.fast};
  &:hover { border-color: ${p => p.theme.colors.primary}; color: ${p => p.theme.colors.primary}; }
  svg { transition: transform 0.3s; }
  &:active svg { transform: rotate(180deg); }
`;

// ── Summary strip ─────────────────────────────────────────────────────────────

const SummaryStrip = styled.div`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: ${p => p.theme.spacing.md};
  margin-bottom: ${p => p.theme.spacing.xl};
  @media (max-width: ${p => p.theme.breakpoints.tablet}) { grid-template-columns: repeat(2, 1fr); }
`;

const SummaryCard = styled.div`
  background: ${p => p.theme.colors.backgroundLight};
  border: 2px solid ${p => p.count > 0 ? p.color + '40' : p.theme.colors.border};
  border-radius: ${p => p.theme.borderRadius.lg};
  padding: ${p => p.theme.spacing.md} ${p => p.theme.spacing.lg};
  display: flex; align-items: center; gap: ${p => p.theme.spacing.md};
  cursor: pointer; transition: ${p => p.theme.transitions.fast};
  &:hover { border-color: ${p => p.color}; box-shadow: 0 4px 16px ${p => p.color}20; }
`;

const SummaryIcon = styled.div`
  width: 40px; height: 40px; border-radius: 10px; flex-shrink: 0;
  background: ${p => p.color}18;
  display: flex; align-items: center; justify-content: center;
  svg { font-size: 1.3rem; color: ${p => p.color}; }
`;

const SummaryInfo = styled.div``;
const SummaryCount = styled.div`
  font-size: 1.5rem; font-weight: 800;
  color: ${p => p.count > 0 ? p.color : p.theme.colors.textMuted};
`;
const SummaryLabel = styled.div`
  font-size: 0.7rem; font-weight: 600; text-transform: uppercase;
  letter-spacing: 0.06em; color: ${p => p.theme.colors.textSecondary};
`;

// ── Section ───────────────────────────────────────────────────────────────────

const Section = styled.div`margin-bottom: ${p => p.theme.spacing.xl};`;

const SectionHeader = styled.div`
  display: flex; align-items: center; gap: 10px;
  margin-bottom: ${p => p.theme.spacing.md};
  svg { font-size: 1.25rem; color: ${p => p.color}; }
`;

const SectionTitle = styled.h2`
  font-size: ${p => p.theme.fontSizes.lg};
  color: ${p => p.theme.colors.textPrimary};
  margin: 0; font-weight: 700;
`;

const SectionCount = styled.span`
  padding: 2px 10px; border-radius: 50px;
  background: ${p => p.color}18; color: ${p => p.color};
  font-size: 0.7rem; font-weight: 700;
`;

const EmptySection = styled.div`
  padding: ${p => p.theme.spacing.lg};
  background: ${p => p.theme.colors.backgroundLight};
  border: 1px solid ${p => p.theme.colors.border};
  border-radius: ${p => p.theme.borderRadius.md};
  text-align: center;
  color: ${p => p.theme.colors.textSecondary};
  font-size: ${p => p.theme.fontSizes.sm};
  display: flex; align-items: center; justify-content: center; gap: 8px;
  svg { color: ${p => p.theme.colors.success}; }
`;

// ── Item card ─────────────────────────────────────────────────────────────────

const ItemGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
  gap: ${p => p.theme.spacing.md};
`;

const ItemCard = styled.div`
  background: ${p => p.theme.colors.backgroundLight};
  border: 1px solid ${p => p.theme.colors.border};
  border-left: 4px solid ${p => p.color};
  border-radius: ${p => p.theme.borderRadius.md};
  padding: ${p => p.theme.spacing.md} ${p => p.theme.spacing.lg};
  cursor: pointer; transition: ${p => p.theme.transitions.fast};
  &:hover { box-shadow: ${p => p.theme.shadows.md}; transform: translateY(-1px); }
`;

const ItemName = styled.div`
  font-weight: 700; font-size: ${p => p.theme.fontSizes.sm};
  color: ${p => p.theme.colors.textPrimary};
  margin-bottom: 2px; white-space: nowrap; overflow: hidden; text-overflow: ellipsis;
`;

const ItemMeta = styled.div`
  font-size: 0.72rem; color: ${p => p.theme.colors.textSecondary};
  display: flex; align-items: center; gap: 8px; flex-wrap: wrap;
`;

const DaysBadge = styled.span`
  padding: 2px 8px; border-radius: 50px;
  background: ${p => p.urgent ? p.theme.colors.danger + '18' : p.theme.colors.warning + '18'};
  color: ${p => p.urgent ? p.theme.colors.danger : p.theme.colors.warning};
  font-size: 0.68rem; font-weight: 700;
`;

const Amount = styled.span`
  font-weight: 600; color: ${p => p.theme.colors.textPrimary};
`;

// ── Component ─────────────────────────────────────────────────────────────────

const fmt = (n) => parseFloat(n || 0).toLocaleString('es-MX', { minimumFractionDigits: 0, maximumFractionDigits: 0 });

const SECTIONS = [
  { key: 'leadsEstancados',        color: '#f59e0b', icon: <MdPersonSearch />,  label: 'Leads estancados',            ref: 'leads' },
  { key: 'recibosPróximos',        color: '#ef4444', icon: <MdReceipt />,        label: 'Recibos por vencer',          ref: 'recibos' },
  { key: 'cotizacionesSinRespuesta', color: '#8b5cf6', icon: <MdRequestQuote />, label: 'Cotizaciones sin respuesta',  ref: 'cotizaciones' },
  { key: 'clientesSinContacto',    color: '#0891b2', icon: <MdPeople />,         label: 'Clientes sin cobro reciente', ref: 'clientes' },
];

const MiDia = () => {
  const navigate = useNavigate();
  const [data, setData]     = useState(null);
  const [loading, setLoading] = useState(true);

  const today = new Date().toLocaleDateString('es-MX', { weekday: 'long', day: 'numeric', month: 'long' });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await miDiaAPI.get();
      setData(res.data?.data || null);
    } catch { /* silencioso */ }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const scrollTo = (ref) => {
    document.getElementById(`section-${ref}`)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  if (loading) {
    return (
      <Container>
        <PageHeader>
          <TitleBlock><Title>Mi Día</Title><Subtitle>Cargando...</Subtitle></TitleBlock>
        </PageHeader>
        <div style={{ textAlign: 'center', padding: '60px', color: '#9ca3af' }}>Cargando tareas del día...</div>
      </Container>
    );
  }

  const resumen = data?.resumen || {};

  return (
    <Container>
      <PageHeader>
        <TitleBlock>
          <Title>Mi Día</Title>
          <Subtitle style={{ textTransform: 'capitalize' }}>{today} · {resumen.total || 0} elementos requieren atención</Subtitle>
        </TitleBlock>
        <RefreshBtn onClick={load} title="Actualizar"><MdRefresh /></RefreshBtn>
      </PageHeader>

      {/* Summary strip */}
      <SummaryStrip>
        {SECTIONS.map(s => (
          <SummaryCard key={s.key} color={s.color} count={resumen[s.key] || 0} onClick={() => scrollTo(s.ref)}>
            <SummaryIcon color={s.color}>{s.icon}</SummaryIcon>
            <SummaryInfo>
              <SummaryCount count={resumen[s.key] || 0} color={s.color}>{resumen[s.key] || 0}</SummaryCount>
              <SummaryLabel>{s.label}</SummaryLabel>
            </SummaryInfo>
          </SummaryCard>
        ))}
      </SummaryStrip>

      {/* ── Leads estancados ── */}
      <Section id="section-leads">
        <SectionHeader color="#f59e0b">
          <MdPersonSearch />
          <SectionTitle>Leads estancados</SectionTitle>
          <SectionCount color="#f59e0b">{data?.leadsEstancados?.length || 0}</SectionCount>
        </SectionHeader>
        {!data?.leadsEstancados?.length ? (
          <EmptySection><MdCheckCircle /> Sin leads estancados</EmptySection>
        ) : (
          <ItemGrid>
            {data.leadsEstancados.map(l => (
              <ItemCard key={l.id} color="#f59e0b" onClick={() => navigate(`/leads/${l.id}`)}>
                <ItemName>{l.nombre}{l.empresa ? ` — ${l.empresa}` : ''}</ItemName>
                <ItemMeta>
                  <DaysBadge urgent={l.dias_sin_actividad > 14}>
                    <MdWarning style={{ fontSize: '0.7rem', verticalAlign: 'middle' }} /> {l.dias_sin_actividad}d sin actividad
                  </DaysBadge>
                  <span style={{ textTransform: 'capitalize' }}>{l.estado}</span>
                  {l.asignado_nombre && <span>{l.asignado_nombre}</span>}
                </ItemMeta>
              </ItemCard>
            ))}
          </ItemGrid>
        )}
      </Section>

      {/* ── Recibos próximos ── */}
      <Section id="section-recibos">
        <SectionHeader color="#ef4444">
          <MdReceipt />
          <SectionTitle>Recibos por vencer</SectionTitle>
          <SectionCount color="#ef4444">{data?.recibosPróximos?.length || 0}</SectionCount>
        </SectionHeader>
        {!data?.recibosPróximos?.length ? (
          <EmptySection><MdCheckCircle /> Sin recibos próximos a vencer</EmptySection>
        ) : (
          <ItemGrid>
            {data.recibosPróximos.map(r => (
              <ItemCard key={r.id} color={r.dias_para_vencer === 0 ? '#ef4444' : '#f59e0b'} onClick={() => navigate(`/recibos/${r.id}`)}>
                <ItemName>{r.cliente_nombre}</ItemName>
                <ItemMeta>
                  <DaysBadge urgent={r.dias_para_vencer <= 1}>
                    {r.dias_para_vencer === 0 ? 'Vence hoy' : `${r.dias_para_vencer}d para vencer`}
                  </DaysBadge>
                  <Amount>${fmt(r.saldo_pendiente)}</Amount>
                  <span>{r.numero_recibo}</span>
                </ItemMeta>
              </ItemCard>
            ))}
          </ItemGrid>
        )}
      </Section>

      {/* ── Cotizaciones sin respuesta ── */}
      <Section id="section-cotizaciones">
        <SectionHeader color="#8b5cf6">
          <MdRequestQuote />
          <SectionTitle>Cotizaciones sin respuesta</SectionTitle>
          <SectionCount color="#8b5cf6">{data?.cotizacionesSinRespuesta?.length || 0}</SectionCount>
        </SectionHeader>
        {!data?.cotizacionesSinRespuesta?.length ? (
          <EmptySection><MdCheckCircle /> Todas las cotizaciones al día</EmptySection>
        ) : (
          <ItemGrid>
            {data.cotizacionesSinRespuesta.map(c => (
              <ItemCard key={c.id} color="#8b5cf6" onClick={() => navigate(`/cotizaciones/${c.id}`)}>
                <ItemName>{c.cliente_nombre}</ItemName>
                <ItemMeta>
                  <DaysBadge urgent={c.dias_sin_respuesta > 10}>
                    {c.dias_sin_respuesta}d sin respuesta
                  </DaysBadge>
                  <Amount>${fmt(c.monto_total)}</Amount>
                  <span style={{ textTransform: 'capitalize' }}>{c.estado}</span>
                </ItemMeta>
              </ItemCard>
            ))}
          </ItemGrid>
        )}
      </Section>

      {/* ── Clientes sin cobro reciente ── */}
      <Section id="section-clientes">
        <SectionHeader color="#0891b2">
          <MdPeople />
          <SectionTitle>Clientes sin cobro reciente</SectionTitle>
          <SectionCount color="#0891b2">{data?.clientesSinContacto?.length || 0}</SectionCount>
        </SectionHeader>
        {!data?.clientesSinContacto?.length ? (
          <EmptySection><MdCheckCircle /> Todos los clientes al corriente</EmptySection>
        ) : (
          <ItemGrid>
            {data.clientesSinContacto.map(c => (
              <ItemCard key={c.id} color="#0891b2" onClick={() => navigate(`/clientes/${c.id}`)}>
                <ItemName>{c.nombre}</ItemName>
                <ItemMeta>
                  <DaysBadge urgent={c.dias_sin_pago > 60 || !c.ultimo_pago}>
                    {c.ultimo_pago ? `${c.dias_sin_pago}d sin pago` : 'Sin pagos'}
                  </DaysBadge>
                  <span>Saldo: <Amount>${fmt(c.saldo_pendiente)}</Amount></span>
                </ItemMeta>
              </ItemCard>
            ))}
          </ItemGrid>
        )}
      </Section>
    </Container>
  );
};

export default MiDia;
